import { jwtDecode } from 'jwt-decode';
import { isAfter, sub, format } from 'date-fns';
import queryString, { type ParsedQuery } from 'query-string';
import { path } from 'ramda';
import {
    COOKIE_KEYS,
    RESPONSE,
} from './_constants';
import type { DateLikeString } from '../types/index';
import type { SearchParams } from '../api/search';
import type {
    CollectionResponse,
    HydraCollection
} from '../api/types/response';
import { Dealership } from '../api/types';

export const isDev = import.meta.env.NODE_ENV === 'development';

export const isTestEnv = typeof import.meta.env.NODE_ENV === 'undefined';

export const parseDealershipTitle = dealer => {
    if (dealer.title.trim().startsWith('-')) {
        return `${dealer.kvps} - ${dealer.title
            .replace('-', '')
            .trim()}`;
    }
    return dealer.title;
};

const getQueryString = () => {
    const urlParamIndex = window.location.href.indexOf('?');

    if (urlParamIndex >= 0) {
        return window.location.href.substr(
            urlParamIndex,
            window.location.href.length
        );
    }

    return '';
};

export const toCapitalizedString = (text: string) =>
    text.replace(text.charAt(0), text.charAt(0).toUpperCase());

export const wait = (time = 100) =>
    new Promise(resolve => setTimeout(() => resolve('ok'), time));

export type SanitizedCollection<T> = {
    list: HydraCollection<T>;
    totalItems: number;
    currentPage: number;
    lastPage: number | undefined;
    totalPages: number;
};

export type CarType = {
    '@id': string;
    imageUrl: string;
    type: string;
};

export type FilterOption = {
    '@id'?: string;
    carTypes?: CarType[];
    model?: string;
    slug?: string;
    count: number;
    key: string;
    imageUrl: string;
};
/**
 * Utilities to help managing pagination
 */
export const getPaginationNumber = (string: string | undefined) =>
    string ? Number(string.split('page=')[1]) : undefined;

export function sanitizeCollectionResponse<T>(
    collectionResponse: CollectionResponse<T>
): SanitizedCollection<T> {
    const currentPage =
        getPaginationNumber(path(RESPONSE.CURRENT_PAGE, collectionResponse)) ||
        1;

    const lastPage =
        getPaginationNumber(path(RESPONSE.LAST_PAGE, collectionResponse)) || 1;

    return {
        list: collectionResponse['hydra:member'] || [],
        totalItems: collectionResponse['hydra:totalItems'],
        currentPage: currentPage || 1,
        lastPage: lastPage,
        totalPages: lastPage
    };
}

export const getId = (item: { '@id': string } | string) => {
    // we get id from iri "/brands/A/channels/a-digital-retail/content-playlists/018e6616-5070-7332-b4d3-35e8745fed85"
    if (typeof item !== 'string' && '@id' in item) {
        return item[RESPONSE.ID].split('/')[6];
    }

    return item.split('/')[6];
};

export default {
    isTestEnv,
    wait,
    getId,
    convertObject(aggregations) {
        const options = {};
        const copy = JSON.parse(JSON.stringify(aggregations));
        for (let i = 0; i < copy.length; i++) {
            const key = Object.keys(copy[i])[0];
            options[key] = copy[i][key];
        }

        return options;
    },

    /**
     *
     * @param value
     * @param delimiter
     * @returns DD/MM/YYYY
     */
    humanDate(value: DateLikeString, delimiter = '/') {
        if (value === '' || typeof value === 'undefined' || value == null) {
            return '';
        }

        const date = new Date(value.toString().split(':')[0].slice(0, -3)); // Safari hack
        let day: number | string = date.getDate();
        let month: number | string = date.getMonth() + 1;

        if (day < 10) {
            day = `0${day}`;
        }

        if (month < 10) {
            month = `0${month}`;
        }
        return `${day}${delimiter}${month}${delimiter}${date.getFullYear()}`;
    },

    humanDuration(duration: number | string) {
        if (!duration) return '00:00';
        const durationInt = +duration;
        const hours = Math.floor(durationInt / 3600);
        const minutes = Math.floor(durationInt / 60) % 60;
        const seconds = durationInt % 60;

        const hh = hours < 10 ? `0${hours}` : (hours as any);
        const mm = minutes < 10 ? `0${minutes}` : minutes;
        const ss = seconds < 10 ? `0${seconds}` : seconds;
        if (hh > 0) {
            return `${hh}:${mm}:${ss}`;
        }
        return `${mm}:${ss}`;
    },

    bytesToSize(bytes: number | string) {
        if (!bytes) return '0 Bytes';
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return '0 Byte';
        const i = Math.floor(Math.log(+bytes) / Math.log(1024));
        const potencial = 1024 ** i;
        return `${Math.round(+bytes / potencial)} ${sizes[i]}`;
    },

    setCookie(cookieName: string, cookieValue: string, expirationHours = 1) {
        const d = new Date();
        d.setTime(d.getTime() + expirationHours * 60 * 60 * 1000);
        const expires = `expires=${d.toUTCString()}`;

        document.cookie = `${cookieName}=${cookieValue};${expires}${
            isDev ? '' : ';samesite=lax;secure;path=/'
        }`;
    },

    removeCookie(cookieName) {
        document.cookie = `${cookieName}=; Max-Age=-99999999`;
    },

    getCookie(cookieName) {
        const name = `${cookieName}=`;
        const decodedCookie = decodeURIComponent(document.cookie);
        const ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i += 1) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return '';
    },

    isTokenExpired(jwtToken) {
        const jwtDecodedToken = jwtDecode(jwtToken) as any;
        const currentTime = new Date().getTime() / 1000;
        return currentTime > jwtDecodedToken.exp;
    },

    isAuthenticated() {
        const token = this.getCookie(COOKIE_KEYS.TOKEN);
        let authenticated = false;
        if (token !== '') {
            authenticated = !this.isTokenExpired(token);
        }
        return authenticated;
    },

    getMinYear(yearsCollection) {
        let minYear = Number.MAX_SAFE_INTEGER;

        if (typeof yearsCollection !== 'undefined') {
            for (let i = 0; i < yearsCollection.length; i += 1) {
                const year = yearsCollection[i].key;
                if (year && year < minYear) {
                    minYear = year;
                }
            }
        }

        return minYear;
    },

    getMaxYear(yearsCollection) {
        let maxYear = Number.MIN_SAFE_INTEGER;
        if (typeof yearsCollection !== 'undefined') {
            for (let i = 0; i < yearsCollection.length; i += 1) {
                const year = yearsCollection[i].key;
                if (year && year > maxYear) {
                    maxYear = year;
                }
            }
        }

        return maxYear;
    },

    getQueryParameter(): SearchParams {
        let query: Record<string, string> | ParsedQuery<string> | string = {};
        const urlParamIndex = window.location.href.indexOf('?');

        if (urlParamIndex >= 0) {
            query = decodeURIComponent(
                window.location.href.substr(
                    urlParamIndex + 1,
                    window.location.href.length
                )
            );
            const queryParams = queryString.parse(query);
            const unmappedQuery = this.unmapCategoryQueryParameter(
                queryParams.query
            );
            queryParams.category = unmappedQuery.category;
            queryParams.query = unmappedQuery.query;
            query = queryParams;
        }

        return query;
    },

    addParamsToUrl(urlPath, params) {
        let paramCurrentCount = 1;
        let url = urlPath;

        const keys = Object.keys(params).filter(
            key =>
                (params[key] &&
                    typeof params[key] === 'object' &&
                    params[key].length > 0) ||
                (typeof params[key] === 'string' && params[key] !== '') ||
                (typeof params[key] === 'number' && params[key] > 0) ||
                (typeof params[key] === 'boolean')

        );

        keys.forEach(key => {
            if (paramCurrentCount === 1) {
                url += '?';
            }

            url += `${key}=${encodeURIComponent(params[key])}`;

            if (paramCurrentCount !== Object.keys(keys).length) {
                url += '&';
            }

            paramCurrentCount += 1;
        });

        return url;
    },

    replaceParamInUrl(url, param, value) {
        return url.replace(`{${param}}`, value);
    },

    setQueryParameter(params: SearchParams) {
        let url = '';
        url = `${window.location.href}`.replace(getQueryString(), '');
        url = this.addParamsToUrl(url, params);
        window.history.pushState({ path: url }, '', url);
    },

    replaceParamsInUrl(params) {
        let url = '';
        url = `${window.location.href}`.replace(getQueryString(), '');
        return this.addParamsToUrl(url, params);
    },

    removeUrlParameter(url, parameter) {
        // prefer to use l.search if you have a location/link object
        const urlparts = url.split('?');
        if (urlparts.length >= 2) {
            const prefix = `${encodeURIComponent(parameter)}=`;
            const pars = urlparts[1].split(/[&;]/g);

            // reverse iteration as may be destructive
            for (let i = pars.length - 1; i > 0; i -= 1) {
                // idiom for string.startsWith
                if (pars[i].lastIndexOf(prefix, 0) !== -1) {
                    pars.splice(i, 1);
                }
            }

            return urlparts[0] + (pars.length > 0 ? `?${pars.join('&')}` : '');
        }
        return url;
    },

    clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
    },

    /**
     * a: array to split into chunks
     * n: number of even numbered chunks the array is split into. The last chunk may be smaller than the others.
     * */
    chunkify(array, chunkLength: number): [FilterOption[]] | [[]] {
        let chunks: any = [];

        if (typeof array !== 'undefined') {
            const length = array.length;
            if (chunkLength > length) {
                return [array];
            }

            let i = 0;

            while (i < length) {
                chunks.push(array.slice(i, i + chunkLength));
                i += chunkLength;
            }
        } else {
            chunks = [[]];
        }

        return chunks;
    },

    addShareToCollection(share, collection) {
        const withShare = share.find(item => item.collection === collection.id);

        if (withShare) {
            return {
                ...collection,
                shared: true,
                shareId: withShare.id
            };
        }

        return {
            ...collection,
            shared: false
        };
    },

    // TODO: When server can read category parameter we can remove the paranthesis-encoding
    mapCategoryQueryParameter(query, categories) {
        if (categories instanceof Array) {
            if (categories.length > 0) {
                let prefix = '';
                if (query !== '') prefix = ' AND';
                return `${prefix}((( (${categories
                    .map(i => (typeof i === 'string' ? i : i.value))
                    .join(' OR ')}) )))`;
            }
        }
        return '';
    },

    unmapCategoryQueryParameter(query) {
        if (typeof query === 'undefined') return { query: '', category: [] };
        let part1 = query.split('AND((( (');
        if (part1.length <= 1) {
            part1 = query.split('((( (');
        }
        if (part1 !== '' && part1.length > 1) {
            return {
                query: part1[0].trim(),
                category: part1[1].split(') )))')[0].split(' OR ')
            };
        }
        return { query, category: [] };
    },

    getOverrideDevices() {
        const storedDevices = localStorage.getItem('overrideDevices');
        if (storedDevices) {
         const overrideDevices = JSON.parse(storedDevices);
        return overrideDevices
        }
        return [];
    },

    getOverrideDeviceType() {
        const storedDeviceType = localStorage.getItem('overrideDeviceType');
        if (storedDeviceType) {
            return storedDeviceType;
        }
        return '';
    },

    getOverrideDealerId() {
        const storedDealerId = localStorage.getItem('overrideDealerId');
        if (storedDealerId) {
            return storedDealerId;
        }
        return '';
    },

    getOverrideDeviceId() {
        const storedDeviceId = localStorage.getItem('overrideDeviceId');
        if (storedDeviceId) {
            return storedDeviceId;
        }
        return '';
    },

    getAssetLicenceStatus(asset) {
        const now = new Date();

        if (!asset?.usageTo) {
            return 'no-restrictions';
        }

        if (asset && asset?.usageTo) {
            const usageTo = new Date(asset.usageTo);
            if (isAfter(now, usageTo)) return 'is-expired';
            if (isAfter(now, sub(usageTo, { weeks: 2 }))) return 'will-expire';
        }

        return 'not-expired';
    },

    getExpirationDate(asset) {
        if (asset && asset.usageTo) {
            const parsedDateString = asset.usageTo.replace(' ', 'T');
            try {
                return format(new Date(parsedDateString), 'yyyy-MM-dd HH:mm');
            } catch (error) {
                return '';
            }
        }
        return '';

        /** Enable once new licences have been released */
        // if (asset.licences) {
        //     const expirationDates = mapLicensesExpirationToMs(asset.licences);
        //     const earliestExpirationTime = getArrMin(expirationDates);
        //     return format(new Date(earliestExpirationTime), 'yyyy-MM-dd HH:mm');
        // }
    },

    uppercaseFirst(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    },

    generateKey(prefix) {
        return `${prefix}_${new Date().getTime()}`;
    },

    sortAlphabetically(a, b) {
        if (a.title < b.title) {
            return -1;
        }
        if (a.title > b.title) {
            return 1;
        }
        return 0;
    },

    getMediaSource(sourceFormats) {
        if (!sourceFormats) return '';
        return sourceFormats.video_2160p ||
            sourceFormats.video_1440p ||
            sourceFormats.video_1080p ||
            sourceFormats.video_0720p ||
            sourceFormats.video_0500p ||
            sourceFormats.video_0396p ||
            sourceFormats.video_0270p ||
            sourceFormats.video_0180p ||
            sourceFormats.web_2160p ||
            sourceFormats.web_1080p ||
            sourceFormats.thumb_0720p ||
            sourceFormats.thumb_0500p ||
            sourceFormats.thumb_0396p ||
            sourceFormats.thumb_0270p
    },

    // Check if the item was added in the last 30 days
    isRecentlyAdded(createdAt) {
        return new Date(createdAt).getTime() > (Date.now() - 30 * 24 * 60 * 60 * 1000);
    },

    mapDealershipOptions: (dealerships: Dealership[], kvpsOnly = false) =>{
        if (!kvpsOnly){    
            return dealerships.map(dealership => ({
                value: dealership['@id'],
                label: parseDealershipTitle(dealership)
            }));
        } else {
            return dealerships.map(dealership => ({
                value: dealership['@id'].split('/')[4],
                label: dealership['@id'].split('/')[4]
            }));
        }
    }
};

